import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { workerCertificationService } from '../../../services/workerCertificationService';
import { workerService } from '../../../services/workerService';
import { useLanguage } from '../../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../../hooks/useSubscriptionReadOnly';

const CERT_TYPES = [
  'CSCS',
  'IPAF',
  'PASMA',
  'First Aid',
  'Plant Operator',
  'Scaffold',
  'Asbestos Awareness',
  'Other',
];

const AddCertificationModal = ({ onClose, onSaved, companyId, prefillWorkerId = null }) => {
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(true);
  const [form, setForm] = useState({
    workerUserId: prefillWorkerId || '',
    certificateType: '',
    certificateNumber: '',
    expiryDate: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      setWorkersLoading(true);
      const result = await workerService?.getAllWorkers();
      const filtered = (result?.data || [])?.filter(w => w?.role === 'worker' || w?.role === 'site_manager');
      setWorkers(filtered);
    } catch (err) {
      console.error('Failed to load workers:', err);
    } finally {
      setWorkersLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFileChange = (e) => {
    const selected = e?.target?.files?.[0];
    if (selected) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(selected?.type)) {
        setError('Only PDF and image files (JPG, PNG, WebP) are allowed.');
        return;
      }
      if (selected?.size > 10 * 1024 * 1024) {
        setError('File size must be under 10MB.');
        return;
      }
      setFile(selected);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (subscriptionReadOnly) {
      setError('Your subscription is read-only. Adding certifications is disabled.');
      return;
    }
    if (!form?.workerUserId) { setError('Please select a worker.'); return; }
    if (!form?.certificateType) { setError('Please select a certificate type.'); return; }
    if (!form?.expiryDate) { setError('Please enter an expiry date.'); return; }

    try {
      setSaving(true);

      let filePath = null;
      if (file) {
        const uploadResult = await workerCertificationService?.uploadCertificateFile(file, form?.workerUserId);
        if (uploadResult?.error) throw uploadResult.error;
        filePath = uploadResult?.data?.path;
      }

      const result = await workerCertificationService?.addCertification({
        workerUserId: form?.workerUserId,
        companyId,
        certificateType: form?.certificateType,
        certificateNumber: form?.certificateNumber || null,
        expiryDate: form?.expiryDate,
        filePath,
      });

      if (result?.error) throw result.error;

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[AddCertificationModal] save failed:', { code: err?.code, message: err?.message, err });
      if (err?.code === '23502') {
        setError(`Failed to save certification — required field missing: ${err?.message}`);
      } else if (err?.code === 'PGRST301') {
        setError(`Failed to save certification — permission denied: ${err?.message}`);
      } else {
        setError(`Failed to save certification: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-elevation-4 w-full max-w-lg border border-border max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="Award" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground font-heading">{t('addCertification')}</h2>
              <p className="text-sm text-muted-foreground">{t('certModalSubtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Worker Selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('certColWorker')} <span className="text-destructive">*</span>
            </label>
            {workersLoading ? (
              <div className="h-10 bg-muted animate-pulse rounded-lg" />
            ) : (
              <select
                value={form?.workerUserId}
                onChange={e => handleChange('workerUserId', e?.target?.value)}
                disabled={!!prefillWorkerId}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
              >
                <option value="">{t('certSelectWorker')}</option>
                {workers?.map(w => (
                  <option key={w?.id} value={w?.id}>
                    {w?.full_name} ({w?.role})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Certificate Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('certColCertType')} <span className="text-destructive">*</span>
            </label>
            <select
              value={form?.certificateType}
              onChange={e => handleChange('certificateType', e?.target?.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">{t('certSelectType')}</option>
              {CERT_TYPES?.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Certificate Number */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('certColCertNumber')} <span className="text-muted-foreground text-xs">({t('certOptional')})</span>
            </label>
            <input
              type="text"
              value={form?.certificateNumber}
              onChange={e => handleChange('certificateNumber', e?.target?.value)}
              placeholder="e.g. CSCS-123456"
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('certColExpiryDate')} <span className="text-destructive">*</span>
            </label>
            <input
              type="date"
              value={form?.expiryDate}
              onChange={e => handleChange('expiryDate', e?.target?.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('certUploadLabel')} <span className="text-muted-foreground text-xs">({t('certUploadHint')})</span>
            </label>
            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-colors">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleFileChange}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="FileCheck" size={18} color="var(--color-success)" />
                  <span className="font-medium">{file?.name}</span>
                  <span className="text-muted-foreground">({(file?.size / 1024)?.toFixed(0)} KB)</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Icon name="Upload" size={22} />
                  <span className="text-sm">{t('certUploadClick')}</span>
                  <span className="text-xs">{t('certUploadMaxSize')}</span>
                </div>
              )}
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button variant="primary" type="submit" loading={saving} disabled={subscriptionReadOnly} iconName="Save" iconPosition="left">
              {t('certSaveCertification')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCertificationModal;
